/**
 * CallbackPage - WorkOS AuthKit OAuth Callback Handler
 *
 * Handles the OAuth callback after customer completes Magic Auth verification:
 * 1. Get WorkOS session and access token
 * 2. Call claim-account on Connect API to link WorkOS user to customer
 * 3. Update auth stage to 'full' (Stage 2)
 * 4. Redirect to dashboard
 */

import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { useAuth } from '@workos-inc/authkit-react';
import { AlertCircle, Circle, LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { Layout } from '@/components/layout/Layout';
import { useTranslation } from '@/hooks/useTranslation';
import { claimCustomerAccountOnConnect, ConnectApiError } from '@/lib/connect-api';

export default function CallbackPage() {
  const navigate = useNavigate();
  const { user, getAccessToken, isLoading } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function claimAccount() {
      // Wait for AuthKit to finish loading
      if (isLoading) return;

      // Check if user is authenticated
      if (!user) {
        setError(t('callback.authenticationFailed'));
        return;
      }

      try {
        // Get WorkOS access token (JWT)
        const accessToken = await getAccessToken();

        if (!accessToken) {
          throw new Error('Failed to get access token');
        }

        // Get access code from localStorage (set during Stage 1 login)
        const accessCode = localStorage.getItem('upgrade_access_code') || localStorage.getItem('access_code');

        if (!accessCode) {
          throw new Error('Access code not found. Please log in again.');
        }

        try {
          await claimCustomerAccountOnConnect(accessCode, accessToken);
        } catch (claimError) {
          console.error('Claim error:', claimError);
          throw new Error(
            claimError instanceof ConnectApiError && claimError.message
              ? claimError.message
              : 'Failed to claim account',
          );
        }

        // Success! Update auth stage to Stage 2
        localStorage.setItem(STORAGE_KEYS.AUTH_STAGE, 'full');
        localStorage.setItem(STORAGE_KEYS.WORKOS_JWT, accessToken);
        localStorage.setItem(STORAGE_KEYS.USER_ID, user.id);

        // Dispatch custom event to notify auth context of localStorage update (same-tab)
        window.dispatchEvent(new Event('localStorageAuthUpdate'));

        // Clean up upgrade-specific items
        localStorage.removeItem('upgrade_email');
        localStorage.removeItem('upgrade_access_code');

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Account claim error:', err);
        setError(err instanceof Error ? err.message : t('callback.failedToComplete'));
      }
    }

    claimAccount();
  }, [user, isLoading, getAccessToken, navigate]);

  if (error) {
    return (
      <Layout showFooter={false}>
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="max-w-md w-full space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/login')} variant="outline" className="flex-1">
                {t('callback.backToLogin')}
              </Button>
              <Button onClick={() => window.location.reload()} className="flex-1">
                {t('callback.tryAgain')}
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="stack flex-1 items-center justify-center">
        <div className="relative">
          <Circle className="size-12 text-muted-foreground/25" />
          <LoaderCircle className="absolute -top-0.5 -left-0.5 size-13 animate-spin text-primary" />
        </div>
        <p className="mt-4 text-base text-muted-foreground text-center">{t('callback.completingSignIn')}</p>
      </div>
    </Layout>
  );
}
